<script setup lang="ts">
import type { Theme } from '../../types/shared'
import type { Breadcrumb, PatchedBreadcrumb } from './types'

type Props = {
  breadcrumbs: Breadcrumb[]
  theme?: Theme
  domain: string
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
})

const breadcrumbsList = computed(() => {
  const normalizedBreadcrumbs = normalizeBreadcrumbs(
    props.breadcrumbs || [],
    props.domain,
  )

  return normalizedBreadcrumbs.map((breadcrumb, index, array) => {
    return {
      value: breadcrumb,
      position: String(index + 1),
      isLink: index !== array.length - 1,
      isSeparator: index < array.length - 1,
      isMobileLast: index === array.length - 2,
    }
  })
})

const componentClasses = computed(() => {
  return ['app-breadcrumbs', `app-breadcrumbs--theme-${props.theme}`]
})

/*
 * Преобразование хлебных крошек
 */
const normalizeBreadcrumbs = (
  breadcrumbs: Breadcrumb[],
  domain: string,
): PatchedBreadcrumb[] => {
  let lastBreadcrumbs = ''
  const normalizeBreadcrumbsList: PatchedBreadcrumb[] = []

  for (const breadcrumb of breadcrumbs) {
    /**
     * 1. Приводим relativeHref к однодому нужному виду?
     * добавляя в начале и конце строки "/"
     * Примеры: "/"" || "/sales/"" || "/sales/podarok/"
     */
    let relativeHref = breadcrumb?.href

    // Если у relativeHref нет в начале черты - добавляем
    if (relativeHref[0] !== '/') {
      relativeHref = '/' + relativeHref
    }

    const relativeHrefIndexLastChar = relativeHref.length - 1
    // Если у relativeHref нет в конце черты - добавляем
    if (relativeHref[relativeHrefIndexLastChar] !== '/') {
      relativeHref += '/'
    }

    /**
     * 2. Добавляем прошлые breadcrumbs для текущего маршрута,
     * если они не были указаны явно
     */
    if (relativeHref.indexOf(lastBreadcrumbs) !== 0) {
      // У relativeHref обрезаем первый символ ("/"),
      // т.к. у lastBreadcrumbs в конце уже имеется "/"
      relativeHref = lastBreadcrumbs + relativeHref.slice(1)
    }

    /**
     * 3. Запоминаем breadcrumbs для следующего маршрута
     * !Важно! lastBreadcrumbs на конце уже имеет "/"
     */
    lastBreadcrumbs = relativeHref

    /**
     * 4. Создаем absoluteHref
     */
    const absoluteHref = `https://${domain}${relativeHref}`

    const normalizeBreadcrumb: PatchedBreadcrumb = {
      ...breadcrumb,
      href: relativeHref,
      relativeHref,
      absoluteHref,
    }
    normalizeBreadcrumbsList.push(normalizeBreadcrumb)
  }

  return normalizeBreadcrumbsList
}
</script>

<template>
  <div :class="componentClasses">
    <ol
      class="app-breadcrumbs__list"
      itemscope
      itemtype="https://schema.org/BreadcrumbList"
    >
      <li
        v-for="breadcrumb in breadcrumbsList"
        :key="breadcrumb.value.href"
        data-test-id="breadcrumb-item"
        class="app-breadcrumbs__item"
        :class="{ 'app-breadcrumbs__item--mobile': breadcrumb.isMobileLast }"
        itemtype="https://schema.org/ListItem"
        itemscope
        itemprop="itemListElement"
      >
        <nuxt-link
          v-if="breadcrumb.isLink"
          class="app-breadcrumbs__link"
          data-test-id="breadcrumb-link"
          :to="breadcrumb.value.href"
          :itemid="breadcrumb.value.absoluteHref"
          :prefetch="false"
          itemtype="https://schema.org/Thing"
          itemscope
          itemprop="item"
        >
          <the-icon
            class="app-breadcrumbs__icon"
            name="expand-right"
            color="var(--ui-primary)"
          />

          <span itemprop="name">{{ breadcrumb.value.text }}</span>
        </nuxt-link>

        <span v-else data-test-id="breadcrumb-text">
          <span itemprop="name">{{ breadcrumb.value.text }}</span>
        </span>

        <meta itemprop="position" :content="breadcrumb.position" />

        <div
          v-if="breadcrumb.isSeparator"
          :key="breadcrumb.value.text"
          class="app-breadcrumbs__separator"
        >
          /
        </div>
      </li>
    </ol>
  </div>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-breadcrumbs {
  &--theme-light {
    color: var(--ui-text-primary);
  }

  &--theme-dark {
    color: var(--ui-white);
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    list-style: none;
  }

  &__item {
    display: flex;
    gap: inherit;

    @include media('<tablet') {
      display: none;

      &--mobile {
        display: flex;
      }
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    color: var(--ui-primary);
    text-decoration: none;
  }

  &__icon {
    margin-right: 4px;
    transform: scale(-1);

    @include media('>=tablet') {
      display: none !important;
    }
  }

  &__separator {
    display: none;

    @include media('>=tablet') {
      display: block;
      color: var(--ui-primary);
    }
  }
}
</style>
